import React from 'react';

/**
 * Renders JSON data in a formatted, color-coded display
 * @param {Object} props - Component props
 * @param {string} props.jsonString - JSON string to be parsed and displayed
 */
const JsonDisplay = ({ jsonString }) => {
  /**
   * Determines text color based on value type
   * @param {*} value - The value to check
   * @returns {string} CSS class for text color
   */
  const getValueColor = (value) => {
    if (value === null) return 'text-danger';
    if (typeof value === 'boolean') return 'text-purple';
    if (typeof value === 'number') return 'text-primary';
    if (typeof value === 'string') return 'text-warning';
    return '';
  };

  /**
   * Recursively renders JSON values with proper indentation and formatting
   * @param {*} value - The value to render
   * @param {number} indent - Current indentation level
   * @returns {JSX.Element} Formatted value
   */
  const renderJsonValue = (value, indent = 0) => {
    if (value === null) {
      return <span className='text-danger'>null</span>;
    }

    if (Array.isArray(value)) {
      return (
        <div style={{ marginLeft: `${indent}px` }}>
          [
          {value.map((item, index) => (
            <div key={index}>
              {renderJsonValue(item, indent + 2)}
              {index < value.length - 1 && ','}
            </div>
          ))}
          ]
        </div>
      );
    }

    if (typeof value === 'object') {
      return (
        <div style={{ marginLeft: `${indent}px` }}>
          {'{'}
          {Object.entries(value).map(([key, val], index, arr) => (
            <div key={key}>
              <span className='text-success'>"{key}"</span>:{' '}
              {renderJsonValue(val, indent + 2)}
              {index < arr.length - 1 && ','}
            </div>
          ))}
          {'}'}
        </div>
      );
    }

    return (
      <span className={getValueColor(value)}>
        {typeof value === 'string' ? `"${value}"` : String(value)}
      </span>
    );
  };

  /**
   * Safely parses JSON string
   * @param {string} str - JSON string to parse
   * @returns {Object|null} Parsed object or null if invalid
   */
  const parseJson = (str) => {
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  };

  const parsedJson = parseJson(jsonString);

  return parsedJson ? (
    <div className='font-monospace small'>{renderJsonValue(parsedJson)}</div>
  ) : (
    <pre className='m-0'>{jsonString}</pre>
  );
};

export default JsonDisplay;
